import React, { useEffect, useRef } from 'react';

// 광고 비활성화 스위치 — true로 바꾸면 모든 광고 다시 활성화
const ADS_ENABLED = false;

// AdSense Publisher ID
const PUBLISHER_ID = 'ca-pub-4468461250716475';

// Extend Window interface for adsbygoogle
declare global {
  interface Window {
    adsbygoogle: unknown[];
  }
}

// 스크립트 로드 상태 추적 (전역)
let isScriptLoaded = false;
let isScriptLoading = false;

/**
 * AdSense 스크립트를 동적으로 로드 (한 번만)
 * 광고가 필요한 페이지에서만 스크립트가 로드됨
 */
const loadAdSenseScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // 이미 로드됨
    if (isScriptLoaded) {
      resolve();
      return;
    }

    // 로딩 중
    if (isScriptLoading) {
      const checkLoaded = setInterval(() => {
        if (isScriptLoaded) {
          clearInterval(checkLoaded);
          resolve();
        }
      }, 100);
      return;
    }

    isScriptLoading = true;

    // 스크립트 동적 생성
    const script = document.createElement('script');
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${PUBLISHER_ID}`;
    script.async = true;
    script.crossOrigin = 'anonymous';

    script.onload = () => {
      isScriptLoaded = true;
      isScriptLoading = false;
      // adsbygoogle 배열 초기화
      window.adsbygoogle = window.adsbygoogle || [];
      resolve();
    };

    script.onerror = () => {
      isScriptLoading = false;
      reject(new Error('AdSense script failed to load'));
    };

    document.head.appendChild(script);
  });
};

interface AdSenseProps {
  adSlot: string;
  adFormat?: 'auto' | 'horizontal' | 'rectangle' | 'vertical';
  fullWidthResponsive?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * AdSense Component
 *
 * 이 컴포넌트가 렌더링될 때만 AdSense 스크립트가 로드됩니다.
 * Memes, MemeDetail, ProjectDetail 페이지에서만 사용하세요.
 */
const AdSense: React.FC<AdSenseProps> = ({
  adSlot,
  adFormat = 'auto',
  fullWidthResponsive = true,
  className = '',
  style = {}
}) => {
  const isAdPushed = useRef(false);

  useEffect(() => {
    if (!ADS_ENABLED || isAdPushed.current) return;

    loadAdSenseScript()
      .then(() => {
        if (!isAdPushed.current && window.adsbygoogle) {
          window.adsbygoogle.push({});
          isAdPushed.current = true;
        }
      })
      .catch((error) => {
        console.log('AdSense not available:', error);
      });
  }, []);

  if (!ADS_ENABLED) return null;

  return (
    <div className={`adsense-container ${className}`} style={style}>
      <ins
        className="adsbygoogle"
        style={{ display: 'block', ...style }}
        data-ad-client={PUBLISHER_ID}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive={fullWidthResponsive.toString()}
      />
    </div>
  );
};

/**
 * In-Article Ad Component
 * 블로그 스타일 콘텐츠 중간에 사용
 */
export const InArticleAd: React.FC<{ adSlot: string; className?: string }> = ({
  adSlot,
  className = ''
}) => {
  const isAdPushed = useRef(false);

  useEffect(() => {
    if (!ADS_ENABLED || isAdPushed.current) return;

    loadAdSenseScript()
      .then(() => {
        if (!isAdPushed.current && window.adsbygoogle) {
          window.adsbygoogle.push({});
          isAdPushed.current = true;
        }
      })
      .catch((error) => {
        console.log('AdSense not available:', error);
      });
  }, []);

  if (!ADS_ENABLED) return null;

  return (
    <div className={`adsense-in-article ${className}`}>
      <ins
        className="adsbygoogle"
        style={{ display: 'block', textAlign: 'center' }}
        data-ad-client={PUBLISHER_ID}
        data-ad-slot={adSlot}
        data-ad-layout="in-article"
        data-ad-format="fluid"
      />
    </div>
  );
};

/**
 * Anchor Ad Component
 * 화면 하단에 고정되는 광고
 */
export const AnchorAd: React.FC<{ adSlot: string }> = ({ adSlot }) => {
  const isAdPushed = useRef(false);
  const [isVisible, setIsVisible] = React.useState(true);

  useEffect(() => {
    if (!ADS_ENABLED || isAdPushed.current) return;

    loadAdSenseScript()
      .then(() => {
        if (!isAdPushed.current && window.adsbygoogle) {
          window.adsbygoogle.push({});
          isAdPushed.current = true;
        }
      })
      .catch((error) => {
        console.log('AdSense not available:', error);
      });
  }, []);

  if (!ADS_ENABLED || !isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-lg border-t border-gray-200">
      <div className="relative max-w-4xl mx-auto">
        <button
          onClick={() => setIsVisible(false)}
          className="absolute -top-8 right-2 bg-gray-800 text-white text-xs px-2 py-1 rounded-t hover:bg-gray-700"
          aria-label="Close ad"
        >
          ✕ 닫기
        </button>
        <ins
          className="adsbygoogle"
          style={{ display: 'block', height: '60px' }}
          data-ad-client={PUBLISHER_ID}
          data-ad-slot={adSlot}
          data-ad-format="horizontal"
          data-full-width-responsive="true"
        />
      </div>
    </div>
  );
};

/**
 * Sidebar Ad Component
 * 사이드바에 고정되는 광고 (데스크톱 전용)
 */
export const SidebarAd: React.FC<{ adSlot: string; className?: string }> = ({
  adSlot,
  className = ''
}) => {
  const isAdPushed = useRef(false);

  useEffect(() => {
    if (!ADS_ENABLED || isAdPushed.current) return;

    loadAdSenseScript()
      .then(() => {
        if (!isAdPushed.current && window.adsbygoogle) {
          window.adsbygoogle.push({});
          isAdPushed.current = true;
        }
      })
      .catch((error) => {
        console.log('AdSense not available:', error);
      });
  }, []);

  if (!ADS_ENABLED) return null;

  return (
    <div className={`adsense-sidebar sticky top-24 ${className}`}>
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={PUBLISHER_ID}
        data-ad-slot={adSlot}
        data-ad-format="vertical"
        data-full-width-responsive="false"
      />
    </div>
  );
};

export default AdSense;
